import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ConfirmDialog } from 'primereact/confirmdialog'

import { useToastContext } from '../../../../../../contexts'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import {
  CustomFieldDefinition,
  requestCreateCustomFieldDefinition,
  requestDeleteCustomFieldDefinition,
  useAllCustomFieldDefinitions,
} from '../../../../master-data-management'
import { SupplierForm } from './Supplier.form'

export const SupplierData: React.FC = () => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()

  const [isBulkDeleteRunning, setIsBulkDeleteRunning] = useState(false)
  const [loadingRowIds, setLoadingRowIds] = useState<Set<string>>(new Set())

  // Get all custom field definitions
  const {
    data: customFieldDefinitions,
    isLoading: isCustomFieldDefinitionsLoading,
    error,
  } = useAllCustomFieldDefinitions()

  const supplierCustomFieldDefinitions = customFieldDefinitions?.filter(
    customFieldDefinition => customFieldDefinition.Type === 'Supplier',
  )
  // const getDisabledFieldIndexByName = (name: string): number => {
  //   const foundIndex = item.Meta?.Fields?.DisabledCalculation?.findIndex(
  //     p => p.Fieldname === name,
  //   )

  //   return foundIndex || -1
  // }

  // const getDisabledFieldValueByName = (name: string): boolean => {
  //   const foundIndex = getDisabledFieldIndexByName(name)
  //   if (foundIndex < 0) return false

  //   const values = item.Meta?.Fields?.DisabledCalculation || []
  //   return values[foundIndex].IsDisabled || false
  // }

  // const replaceDisabledFields = (name: string, value: boolean) => {
  //   const values = [...(item.Meta?.Fields?.DisabledCalculation || [])]
  //   const foundIndex = values.findIndex(p => p.Fieldname === name)
  //   if (foundIndex >= 0) {
  //     values.splice(foundIndex, 1)
  //   }

  //   values.push({ Fieldname: name, IsDisabled: value })
  //   return values
  // }

  // Functions for handling the loading states of the buttons
  const addLoadingRowIds = (rowIds: string[]) => {
    setLoadingRowIds(prevState => {
      const updatedSet = new Set(prevState)
      rowIds.forEach(rowId => updatedSet.add(rowId))
      return updatedSet
    })
  }

  const removeLoadingRowIds = (rowIds: string[]) => {
    setLoadingRowIds(prevState => {
      const updatedSet = new Set(prevState)
      rowIds.forEach(rowId => updatedSet.delete(rowId))
      return updatedSet
    })
  }

  const hasLoadingRowId = (rowId: string) => {
    return loadingRowIds.has(rowId)
  }

  const handleCreate = async (customFieldDefinition: CustomFieldDefinition) => {
    try {
      addLoadingRowIds([customFieldDefinition.Id])

      await requestCreateCustomFieldDefinition(customFieldDefinition)

      showToast({
        severity: 'success',
        summary: t(
          'buyerSettings.additionalProperties.supplier.table.add.messages.success.summary',
        ),
        detail: t(
          'buyerSettings.additionalProperties.supplier.table.add.messages.success.detail',
          {
            key: customFieldDefinition?.Key,
          },
        ),
      })
    } catch (error) {
      const handleableError = getHandleableError(error)
      const errorMessage = handleableError.message

      showToast({
        severity: 'error',
        summary: t(
          'buyerSettings.additionalProperties.supplier.table.add.messages.failure.summary',
        ),
        detail: errorMessage,
        sticky: true,
      })
    } finally {
      removeLoadingRowIds([customFieldDefinition.Id])
    }
  }

  const handleDelete = async (
    customFieldDefinitions: CustomFieldDefinition[],
    callback?: () => void,
  ) => {
    const customFieldDefinitionsIds = customFieldDefinitions.map(
      customFieldDefinition => customFieldDefinition.Id,
    )

    try {
      addLoadingRowIds(customFieldDefinitionsIds)
      setIsBulkDeleteRunning(customFieldDefinitionsIds.length > 1)

      const deleteCustomFieldDefinitions = async () => {
        const deleteRequests = customFieldDefinitionsIds.map(
          customFieldDefinitionsId =>
            requestDeleteCustomFieldDefinition(customFieldDefinitionsId),
        )

        await Promise.all(deleteRequests)

        if (callback) callback()

        showToast({
          severity: 'success',
          summary: t(
            'buyerSettings.additionalProperties.supplier.table.delete.messages.success.summary',
          ),
          detail: t(
            'buyerSettings.additionalProperties.supplier.table.delete.messages.success.detail',
            {
              count: customFieldDefinitions?.length,
              key: customFieldDefinitions[0].Key,
            },
          ),
        })
      }

      await deleteCustomFieldDefinitions()
    } catch (error) {
      const handleableError = getHandleableError(error)
      const errorMessage = handleableError.message

      showToast({
        severity: 'error',
        summary: t(
          'buyerSettings.additionalProperties.supplier.table.delete.messages.failure.summary',
        ),
        detail: errorMessage,
        sticky: true,
      })
    } finally {
      removeLoadingRowIds(customFieldDefinitionsIds)
      setIsBulkDeleteRunning(false)
    }
  }

  return (
    <>
      <SupplierForm
        customFieldDefinitions={supplierCustomFieldDefinitions}
        isLoading={isCustomFieldDefinitionsLoading}
        hasLoadingRowId={hasLoadingRowId}
        isBulkDeleteRunning={isBulkDeleteRunning}
        handleCreate={handleCreate}
        handleDelete={handleDelete}
      />

      <ConfirmDialog />
    </>
  )
}
